import { Box } from '@sequencehq/core-components'
import { FC, useCallback, useEffect, useRef } from 'react'

import { type ReactFlowState, useStore } from '@xyflow/react'
import { useAtomValue, useSetAtom } from 'jotai'
import {
  addNodeFromConnectionAtom,
  connectionDropAtom,
  connectionDropPreviewTypeAtom
} from 'Automations/automationEditor/atoms'
import { useConnectionDropNodeItems } from '../../hooks'
import { NodePickerBar } from './NodePickerBar'
import {
  CONNECTION_PREVIEW_SKELETON_HEIGHT,
  CONNECTION_PREVIEW_SKELETON_WIDTH
} from './connectionPreview.constants'

const GAP_BELOW_NODE_PX = 16
const zoomSelector = (state: ReactFlowState) => state.transform[2]

/**
 * The node picker shown when a connection is dragged from a source handle and
 * dropped on empty canvas. Anchored at the drop point; hovering an option
 * previews the node in the canvas; selecting one creates it connected to the
 * dragged source. Dismisses on outside click or Escape.
 */
export const ConnectionDropPicker: FC = () => {
  const drop = useAtomValue(connectionDropAtom)
  const items = useConnectionDropNodeItems()
  const addNode = useSetAtom(addNodeFromConnectionAtom)
  const setDrop = useSetAtom(connectionDropAtom)
  const setPreviewType = useSetAtom(connectionDropPreviewTypeAtom)
  const zoom = useStore(zoomSelector)
  const ref = useRef<HTMLDivElement>(null)

  const dismiss = useCallback(() => {
    setDrop(null)
    setPreviewType(null)
  }, [setDrop, setPreviewType])

  const isOpen = Boolean(drop)

  useEffect(() => {
    if (!isOpen) return

    const onPointerDown = (event: PointerEvent) => {
      if (!ref.current?.contains(event.target as Node)) dismiss()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') dismiss()
    }

    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen, dismiss])

  if (!drop) return null

  // The node is centred vertically on the drop point (its left-edge handle sits
  // there), and its left edge sits at the drop point. Anchor the bar below the
  // skeleton's bottom edge — half a node-height below the drop point — centred
  // on it.
  const left = drop.clientX + (CONNECTION_PREVIEW_SKELETON_WIDTH * zoom) / 2
  const top =
    drop.clientY +
    (CONNECTION_PREVIEW_SKELETON_HEIGHT * zoom) / 2 +
    GAP_BELOW_NODE_PX

  return (
    <Box
      ref={ref}
      position="fixed"
      left={`${left}px`}
      top={`${top}px`}
      transform="translateX(-50%)"
      zIndex="popover"
    >
      <NodePickerBar
        data-testid="automations.editor.connectionDrop.bar"
        items={items}
        itemTestId={nodeType => `automations.editor.connectionDrop.${nodeType}`}
        onHoverItem={setPreviewType}
        onSelect={item =>
          addNode({ nodeType: item.nodeType, config: item.config })
        }
      />
    </Box>
  )
}
